import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button, Card, Col, DatePicker, Form, Grid, Input, InputNumber, Modal,
    Popconfirm, Row, Select, Space, Statistic, Table, Tabs, Tag, Typography, Upload, message,
} from 'antd';
import {
    CheckCircleOutlined, DeleteOutlined, EditOutlined, GiftOutlined,
    DownloadOutlined, PlusOutlined, PrinterOutlined, SendOutlined, UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import RiceAllowanceMonthlyUpload from '../components/RiceAllowanceMonthlyUpload';

const { Title, Text } = Typography;

const statusColor = { DRAFT: 'default', APPROVED: 'processing', RELEASED: 'success', CANCELLED: 'error' };
const normalizeItemName = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const MAX_RELEASE_ATTACHMENTS = 5;
const MAX_RELEASE_ATTACHMENT_MB = 10;
const RELEASE_ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
}[character]));
const allowanceGroup = (row) => {
    const kg = Number(row.allowance_kg);
    if (Math.abs(kg - Number(row.original_allowance_kg)) > 0.001) return 'override';
    if (kg === 25) return '25';
    if (kg === 50) return '50';
    return 'override';
};

const RiceAllowancePage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user, hasPermission } = useAuth();
    const canManage = ['ADMIN', 'MANAGER', 'STAFF'].includes(user?.role);
    const canDelete = ['ADMIN', 'MANAGER'].includes(user?.role);
    const [form] = Form.useForm();
    const [releaseForm] = Form.useForm();
    const [batches, setBatches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBatch, setEditingBatch] = useState(null);
    const [recipientRows, setRecipientRows] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [group, setGroup] = useState('all');
    const [search, setSearch] = useState('');
    const [bulkReason, setBulkReason] = useState('');
    const [releaseModalOpen, setReleaseModalOpen] = useState(false);
    const [releaseTarget, setReleaseTarget] = useState(null);
    const [releasing, setReleasing] = useState(false);
    const [releaseAttachments, setReleaseAttachments] = useState([]);
    const [additionalAttachments, setAdditionalAttachments] = useState([]);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [monthlyUploadOpen, setMonthlyUploadOpen] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [batchRes, employeeRes, branchRes, locationRes, itemRes] = await Promise.all([
                client.get('/transactions/rice-allowances'),
                client.get('/transactions/rice-allowances/employees'),
                client.get('/master/branches'),
                client.get('/master/locations'),
                client.get('/master/items'),
            ]);
            setBatches(batchRes.data || []);
            setEmployees(employeeRes.data || []);
            setBranches(branchRes.data || []);
            setLocations(locationRes.data || []);
            setItems((itemRes.data || []).filter(item => item.is_active && item.category?.slug === 'rice'));
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load rice allowance data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const wellMilledItem = useMemo(
        () => items.find(item => normalizeItemName(item.name) === 'wellmilled'),
        [items]
    );

    const rowsForBranch = useCallback((branchId) => employees
        .filter(employee => !employee.branch_id || Number(employee.branch_id) === Number(branchId))
        .map(employee => {
            const defaultKg = Number(employee.rice_allowance_kg || 25);
            return {
                employee_id: employee.id,
                employee,
                original_allowance_kg: defaultKg,
                allowance_kg: defaultKg,
                item_id: wellMilledItem?.id,
                unit_price: Number(wellMilledItem?.base_price || 0),
                override_reason: '',
            };
        }), [employees, wellMilledItem]);

    const openCreate = () => {
        if (!wellMilledItem) {
            message.error('The active Well-Milled rice item was not found. Add or activate it before creating an allowance batch.');
            return;
        }
        const branchId = branches[0]?.id;
        const branchLocations = locations.filter(location => Number(location.branch_id) === Number(branchId));
        const rows = rowsForBranch(branchId);
        setEditingBatch(null);
        setRecipientRows(rows);
        setSelectedIds(rows.map(row => row.employee_id));
        setGroup('all');
        setSearch('');
        setBulkReason('');
        form.resetFields();
        form.setFieldsValue({
            allowance_month: dayjs().startOf('month'),
            branch_id: branchId,
            location_id: branchLocations[0]?.id,
            item_id: wellMilledItem.id,
        });
        setModalOpen(true);
    };

    const openBatch = async (batch) => {
        try {
            setAdditionalAttachments([]);
            const { data } = await client.get(`/transactions/rice-allowances/${batch.id}`);
            setEditingBatch(data);
            setRecipientRows((data.recipients || []).map(row => ({ ...row, employee: row.employee })));
            setSelectedIds((data.recipients || []).map(row => row.employee_id));
            setGroup('all');
        setSearch('');
        setBulkReason('');
            form.setFieldsValue({
                allowance_month: dayjs(data.allowance_month),
                branch_id: data.branch_id,
                location_id: data.location_id,
                item_id: data.item_id,
                remarks: data.remarks,
            });
            setModalOpen(true);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load batch');
        }
    };

    const isEditable = canManage && (!editingBatch || editingBatch.status === 'DRAFT');
    const selectedRows = useMemo(() => recipientRows.filter(row => selectedIds.includes(row.employee_id)), [recipientRows, selectedIds]);
    const summary = useMemo(() => ({
        count25: selectedRows.filter(row => allowanceGroup(row) === '25').length,
        kg25: selectedRows.filter(row => allowanceGroup(row) === '25').reduce((sum, row) => sum + Number(row.allowance_kg), 0),
        count50: selectedRows.filter(row => allowanceGroup(row) === '50').length,
        kg50: selectedRows.filter(row => allowanceGroup(row) === '50').reduce((sum, row) => sum + Number(row.allowance_kg), 0),
        overrides: selectedRows.filter(row => allowanceGroup(row) === 'override').length,
        totalKg: selectedRows.reduce((sum, row) => sum + Number(row.allowance_kg || 0), 0),
        totalAmount: selectedRows.reduce((sum, row) => sum + (Number(row.allowance_kg || 0) * Number(row.unit_price || 0)), 0),
    }), [selectedRows]);

    const visibleRows = useMemo(() => recipientRows.filter(row => {
        const matchesGroup = group === 'all' || allowanceGroup(row) === group;
        const matchesSearch = (row.employee?.name || '').toLowerCase().includes(search.toLowerCase());
        return matchesGroup && matchesSearch;
    }), [recipientRows, group, search]);

    const updateRow = (employeeId, field, value) => {
        setRecipientRows(rows => rows.map(row => row.employee_id === employeeId ? { ...row, [field]: value } : row));
    };

    const handleBranchChange = (branchId) => {
        const rows = rowsForBranch(branchId);
        setRecipientRows(rows);
        setSelectedIds(rows.map(row => row.employee_id));
        const branchLocations = locations.filter(location => Number(location.branch_id) === Number(branchId));
        form.setFieldValue('location_id', branchLocations[0]?.id);
    };

    const handleRecipientItemChange = (employeeId, itemId) => {
        const selectedItem = items.find(item => Number(item.id) === Number(itemId));
        setRecipientRows(rows => rows.map(row => row.employee_id === employeeId ? {
            ...row,
            item_id: itemId,
            unit_price: Number(selectedItem?.base_price || 0),
        } : row));
    };

    const bulkSetKg = (kg) => setRecipientRows(rows => rows.map(row => (
        selectedIds.includes(row.employee_id) ? { ...row, allowance_kg: kg } : row
    )));

    const applyBulkReason = () => {
        if (!bulkReason.trim()) return message.warning('Enter a reason to apply.');
        setRecipientRows(rows => rows.map(row => (
            selectedIds.includes(row.employee_id)
            && Math.abs(Number(row.allowance_kg) - Number(row.original_allowance_kg)) > 0.001
                ? { ...row, override_reason: bulkReason.trim() }
                : row
        )));
    };

    const saveBatch = async (values) => {
        if (!selectedRows.length) {
            message.error('Select at least one employee.');
            return;
        }
        const incompleteSaleLine = selectedRows.find(row => !row.item_id || Number(row.unit_price) <= 0);
        if (incompleteSaleLine) {
            message.error(`Select a rice item and valid price for ${incompleteSaleLine.employee?.name}.`);
            return;
        }
        const missingReason = selectedRows.find(row =>
            Math.abs(Number(row.allowance_kg) - Number(row.original_allowance_kg)) > 0.001
            && !row.override_reason?.trim()
        );
        if (missingReason) {
            message.error(`Enter an override reason for ${missingReason.employee?.name}.`);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...values,
                allowance_month: values.allowance_month.startOf('month').format('YYYY-MM-DD'),
                recipients: selectedRows.map(row => ({
                    employee_id: row.employee_id,
                    item_id: row.item_id,
                    allowance_kg: Number(row.allowance_kg),
                    unit_price: Number(row.unit_price),
                    override_reason: row.override_reason || null,
                })),
            };
            if (editingBatch) {
                await client.put(`/transactions/rice-allowances/${editingBatch.id}`, payload);
                message.success('Rice allowance batch updated');
            } else {
                await client.post('/transactions/rice-allowances', payload);
                message.success('Rice allowance batch created');
            }
            setModalOpen(false);
            loadData();
        } catch (error) {
            const errors = error.response?.data?.errors;
            message.error(errors ? Object.values(errors).flat()[0] : (error.response?.data?.message || 'Failed to save batch'));
        } finally {
            setSaving(false);
        }
    };

    const transition = async (batch, action, payload = {}) => {
        try {
            await client.post(`/transactions/rice-allowances/${batch.id}/${action}`, payload);
            message.success(action === 'approve' ? 'Batch approved' : 'Batch released and inventory deducted');
            setModalOpen(false);
            loadData();
            return true;
        } catch (error) {
            const errors = error.response?.data?.errors;
            message.error(errors ? Object.values(errors).flat()[0] : (error.response?.data?.message || `Failed to ${action} batch`));
            return false;
        }
    };

    const openReleaseModal = (batch) => {
        setReleaseTarget(batch);
        setReleaseAttachments([]);
        releaseForm.resetFields();
        setReleaseModalOpen(true);
    };

    const releaseBatch = async (values) => {
        if (!releaseTarget) return;
        setReleasing(true);
        try {
            const payload = new FormData();
            payload.append('reference_number', values.reference_number.trim());
            releaseAttachments.forEach(file => payload.append('attachments[]', file.originFileObj || file));
            await client.post(`/transactions/rice-allowances/${releaseTarget.id}/release`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            message.success('Batch released, sales posted, and attachments saved');
            setReleaseModalOpen(false);
            setReleaseTarget(null);
            setReleaseAttachments([]);
            setModalOpen(false);
            loadData();
        } catch (error) {
            const errors = error.response?.data?.errors;
            message.error(errors ? Object.values(errors).flat()[0] : (error.response?.data?.message || 'Failed to release batch'));
        } finally {
            setReleasing(false);
        }
    };

    const downloadAttachment = async (batchId, index, fileName) => {
        try {
            const response = await client.get(`/transactions/rice-allowances/${batchId}/attachments/${index}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || `rice-allowance-attachment-${index + 1}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to download attachment');
        }
    };

    const uploadAdditionalAttachments = async () => {
        if (!editingBatch || !additionalAttachments.length) return;
        setUploadingAttachments(true);
        try {
            const payload = new FormData();
            additionalAttachments.forEach(file => payload.append('attachments[]', file.originFileObj || file));
            const { data } = await client.post(
                `/transactions/rice-allowances/${editingBatch.id}/attachments`,
                payload,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setEditingBatch(data);
            setRecipientRows((data.recipients || []).map(row => ({ ...row, employee: row.employee })));
            setAdditionalAttachments([]);
            message.success('Release attachments uploaded');
            loadData();
        } catch (error) {
            const errors = error.response?.data?.errors;
            message.error(errors ? Object.values(errors).flat()[0] : (error.response?.data?.message || 'Failed to upload attachments'));
        } finally {
            setUploadingAttachments(false);
        }
    };

    const deleteBatch = async (id) => {
        try {
            await client.delete(`/transactions/rice-allowances/${id}`);
            message.success('Draft batch deleted');
            loadData();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to delete batch');
        }
    };

    const printBatch = async (batch) => {
        const { data } = await client.get(`/transactions/rice-allowances/${batch.id}`);
        const groups = [
            ['25 kg recipients', data.recipients.filter(row => allowanceGroup(row) === '25')],
            ['50 kg recipients', data.recipients.filter(row => allowanceGroup(row) === '50')],
            ['Overrides', data.recipients.filter(row => allowanceGroup(row) === 'override')],
        ].filter(([, rows]) => rows.length);
        const printable = groups.map(([label, rows]) => `
            <h2>${label}</h2><table><thead><tr><th>#</th><th>Employee</th><th>KG</th><th>Reason</th><th>Signature</th></tr></thead>
            <tbody>${rows.map((row, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(row.employee?.name)}</td><td>${Number(row.allowance_kg).toLocaleString()}</td><td>${escapeHtml(row.override_reason)}</td><td></td></tr>`).join('')}</tbody></table>
        `).join('');
        const popup = window.open('', '_blank', 'width=900,height=700');
        if (!popup) return message.warning('Allow pop-ups to print the distribution list.');
        popup.document.write(`<html><head><title>${escapeHtml(data.batch_number)}</title><style>body{font-family:Arial;padding:28px}h1{margin-bottom:4px}h2{margin-top:28px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #999;padding:9px;text-align:left}th{background:#eee}td:last-child{height:34px}</style></head><body><h1>${escapeHtml(data.batch_number)}</h1><div>${dayjs(data.allowance_month).format('MMMM YYYY')} · ${escapeHtml(data.branch?.name)} · Total ${Number(data.total_quantity).toLocaleString()} kg</div>${data.reference_number ? `<div><strong>Release Reference:</strong> ${escapeHtml(data.reference_number)}</div>` : ''}${printable}</body></html>`);
        popup.document.close();
        popup.focus();
        popup.print();
    };

    const batchColumns = [
        { title: 'Batch', dataIndex: 'batch_number', render: value => <Text strong>{value}</Text> },
        { title: 'Reference', dataIndex: 'reference_number', render: value => value || '—' },
        { title: 'Month', dataIndex: 'allowance_month', render: value => dayjs(value).format('MMMM YYYY') },
        { title: 'Branch', render: (_, row) => row.branch?.name },
        { title: 'Rice Item', render: (_, row) => row.item?.name },
        { title: 'Employees', dataIndex: 'total_employees', align: 'right' },
        { title: 'Total KG', dataIndex: 'total_quantity', align: 'right', render: value => `${Number(value).toLocaleString()} kg` },
        { title: 'Status', dataIndex: 'status', render: value => <Tag color={statusColor[value]}>{value}</Tag> },
        {
            title: 'Actions', fixed: 'right', render: (_, row) => <Space>
                <Button icon={row.status === 'DRAFT' ? <EditOutlined /> : <CheckCircleOutlined />} onClick={() => openBatch(row)}>{row.status === 'DRAFT' ? 'Edit' : 'View'}</Button>
                <Button icon={<PrinterOutlined />} onClick={() => printBatch(row)} />
                {row.status === 'DRAFT' && canDelete && <Popconfirm title="Delete this draft batch?" onConfirm={() => deleteBatch(row.id)}><Button danger icon={<DeleteOutlined />} /></Popconfirm>}
            </Space>,
        },
    ];

    const recipientColumns = [
        { title: 'Employee', render: (_, row) => <div><Text strong>{row.employee?.name}</Text><br /><Text type="secondary">{row.employee?.code}</Text></div> },
        { title: 'Default', dataIndex: 'original_allowance_kg', width: 95, render: value => `${Number(value)} kg` },
        {
            title: 'Batch KG', width: 130, render: (_, row) => <InputNumber
                min={0.01} max={1000} precision={2} value={row.allowance_kg} disabled={!isEditable}
                onChange={value => updateRow(row.employee_id, 'allowance_kg', value)} style={{ width: '100%' }}
            />,
        },
        {
            title: 'Rice Item', width: 190, render: (_, row) => <Select
                value={row.item_id} disabled={!isEditable} style={{ width: '100%' }}
                options={items.map(item => ({ label: item.name, value: item.id }))}
                onChange={itemId => handleRecipientItemChange(row.employee_id, itemId)}
            />,
        },
        {
            title: 'Price', width: 135, render: (_, row) => <InputNumber
                min={0.01} max={999999999} precision={2} prefix="₱" value={row.unit_price}
                disabled={!isEditable} style={{ width: '100%' }}
                onChange={value => updateRow(row.employee_id, 'unit_price', value)}
            />,
        },
        {
            title: 'Override reason', render: (_, row) => {
                const overridden = Math.abs(Number(row.allowance_kg) - Number(row.original_allowance_kg)) > 0.001;
                return <Input value={row.override_reason} disabled={!isEditable || !overridden} status={overridden && !row.override_reason?.trim() ? 'error' : ''}
                    placeholder={overridden ? 'Required reason' : 'Not required'} onChange={event => updateRow(row.employee_id, 'override_reason', event.target.value)} />;
            },
        },
    ];

    return <div style={{ padding: isMobile ? 16 : '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 24 }}>
            <div><Title level={2} style={{ margin: 0 }}>Rice Allowance</Title><Text type="secondary">Prepare and release monthly employee rice allowances.</Text></div>
            {canManage && <Space wrap>
                <Button size="large" icon={<UploadOutlined />} onClick={() => setMonthlyUploadOpen(true)}>Monthly Upload</Button>
                <Button type="primary" size="large" icon={<PlusOutlined />} onClick={openCreate}>New Monthly Batch</Button>
            </Space>}
        </div>
        <Card><Table rowKey="id" columns={batchColumns} dataSource={batches} loading={loading} scroll={{ x: 1050 }} /></Card>

        {canManage && <RiceAllowanceMonthlyUpload
            open={monthlyUploadOpen}
            onClose={() => setMonthlyUploadOpen(false)}
            onCompleted={loadData}
            branches={branches}
            locations={locations}
            items={items}
        />}

        <Modal title={editingBatch ? `${editingBatch.batch_number} · ${editingBatch.status}` : 'New Rice Allowance Batch'} open={modalOpen}
            onCancel={() => setModalOpen(false)} width={1200} footer={null} destroyOnHidden>
            <Form form={form} layout="vertical" onFinish={saveBatch} disabled={!isEditable}>
                <Row gutter={16}>
                    <Col xs={24} md={8}><Form.Item name="allowance_month" label="Allowance Month" rules={[{ required: true }]}><DatePicker picker="month" style={{ width: '100%' }} /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}><Select options={branches.map(row => ({ label: row.name, value: row.id }))} onChange={handleBranchChange} /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item noStyle shouldUpdate>{({ getFieldValue }) => <Form.Item name="location_id" label="Source Warehouse" rules={[{ required: true }]}><Select options={locations.filter(row => Number(row.branch_id) === Number(getFieldValue('branch_id'))).map(row => ({ label: row.name, value: row.id }))} /></Form.Item>}</Form.Item></Col>
                </Row>
                <Form.Item name="item_id" hidden><Input /></Form.Item>
                <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
                {editingBatch?.reference_number && <Form.Item label="Release Reference Number"><Input value={editingBatch.reference_number} disabled /></Form.Item>}
            </Form>
            {editingBatch?.status === 'RELEASED' && (
                <div style={{ marginBottom: 16, padding: 14, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                    <Text strong>Release Attachments</Text>
                    {!!editingBatch.release_attachments?.length ? (
                        <Space wrap size={6} style={{ display: 'flex', marginTop: 8 }}>
                            {editingBatch.release_attachments.map((attachment, index) => (
                                <Button
                                    key={`${attachment.path}-${index}`}
                                    icon={<DownloadOutlined />}
                                    onClick={() => downloadAttachment(editingBatch.id, index, attachment.original_name)}
                                >
                                    {attachment.original_name || `Attachment ${index + 1}`}
                                </Button>
                            ))}
                        </Space>
                    ) : (
                        <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>No attachments uploaded.</Text>
                    )}
                    {hasPermission('post_transaction') && (editingBatch.release_attachments?.length || 0) < MAX_RELEASE_ATTACHMENTS && (
                        <Space align="start" wrap style={{ display: 'flex', marginTop: 12 }}>
                            <Upload
                                fileList={additionalAttachments}
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                multiple
                                maxCount={MAX_RELEASE_ATTACHMENTS - (editingBatch.release_attachments?.length || 0)}
                                beforeUpload={(file) => {
                                    if (!RELEASE_ATTACHMENT_TYPES.includes(file.type)) {
                                        message.error('Only PDF, JPG, PNG, or WEBP files are accepted.');
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (file.size / 1024 / 1024 > MAX_RELEASE_ATTACHMENT_MB) {
                                        message.error(`Each attachment must be ${MAX_RELEASE_ATTACHMENT_MB} MB or smaller.`);
                                        return Upload.LIST_IGNORE;
                                    }
                                    return false;
                                }}
                                onChange={({ fileList }) => setAdditionalAttachments(fileList.slice(
                                    0,
                                    MAX_RELEASE_ATTACHMENTS - (editingBatch.release_attachments?.length || 0)
                                ))}
                            >
                                <Button icon={<UploadOutlined />}>Choose Files</Button>
                            </Upload>
                            <Button
                                type="primary"
                                loading={uploadingAttachments}
                                disabled={!additionalAttachments.length}
                                onClick={uploadAdditionalAttachments}
                            >
                                Upload Attachments
                            </Button>
                        </Space>
                    )}
                    <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        Up to 5 files per release, 10 MB each. Accepted: PDF, JPG, PNG, and WEBP.
                    </Text>
                </div>
            )}

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}><Card size="small"><Statistic title="25 kg group" value={summary.count25} suffix={`staff / ${summary.kg25} kg`} /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title="50 kg group" value={summary.count50} suffix={`staff / ${summary.kg50} kg`} /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title="Overrides" value={summary.overrides} suffix="staff" /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title="Batch Total" value={summary.totalKg} suffix="kg" /><Text strong>₱{summary.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Card></Col>
            </Row>

            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search placeholder="Search employee" allowClear onChange={event => setSearch(event.target.value)} style={{ width: 240 }} />
                {isEditable && <><Button onClick={() => setSelectedIds(recipientRows.map(row => row.employee_id))}>Select All</Button><Button onClick={() => setSelectedIds([])}>Clear</Button><Button onClick={() => bulkSetKg(25)}>Set selected to 25 kg</Button><Button onClick={() => bulkSetKg(50)}>Set selected to 50 kg</Button><Input placeholder="Bulk override reason" value={bulkReason} onChange={event => setBulkReason(event.target.value)} style={{ width: 220 }} /><Button onClick={applyBulkReason}>Apply reason</Button></>}
            </Space>
            <Tabs activeKey={group} onChange={setGroup} items={[
                { key: 'all', label: `All (${selectedRows.length})` },
                { key: '25', label: `25 kg (${summary.count25})` },
                { key: '50', label: `50 kg (${summary.count50})` },
                { key: 'override', label: `Overrides (${summary.overrides})` },
            ]} />
            <Table rowKey="employee_id" size="small" columns={recipientColumns} dataSource={visibleRows} pagination={{ pageSize: 10 }} scroll={{ x: 1100 }}
                rowSelection={isEditable ? { selectedRowKeys: selectedIds, preserveSelectedRowKeys: true, onChange: keys => setSelectedIds(keys) } : undefined} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <Button onClick={() => setModalOpen(false)}>Close</Button>
                {isEditable && <Button type="primary" loading={saving} icon={<GiftOutlined />} onClick={() => form.submit()}>{editingBatch ? 'Save Changes' : 'Create Draft'}</Button>}
                {editingBatch?.status === 'DRAFT' && hasPermission('approve_transaction') && <Popconfirm title="Approve and lock this batch?" onConfirm={() => transition(editingBatch, 'approve')}><Button icon={<CheckCircleOutlined />}>Approve</Button></Popconfirm>}
                {editingBatch?.status === 'APPROVED' && hasPermission('post_transaction') && <Button type="primary" icon={<SendOutlined />} onClick={() => openReleaseModal(editingBatch)}>Release Batch</Button>}
            </div>
        </Modal>

        <Modal
            title="Release Rice Allowance Batch"
            open={releaseModalOpen}
            onCancel={() => !releasing && setReleaseModalOpen(false)}
            onOk={() => releaseForm.submit()}
            okText="Release and Post Inventory"
            confirmLoading={releasing}
            closable={!releasing}
            maskClosable={!releasing}
        >
            <Text type="secondary">
                Enter the acknowledgement, voucher, or distribution reference for {releaseTarget?.batch_number}.
            </Text>
            <Form form={releaseForm} layout="vertical" onFinish={releaseBatch} style={{ marginTop: 16 }}>
                <Form.Item
                    name="reference_number"
                    label="Release Reference Number"
                    rules={[
                        { required: true, whitespace: true, message: 'Reference number is required' },
                        { max: 255, message: 'Reference number cannot exceed 255 characters' },
                    ]}
                >
                    <Input autoFocus placeholder="e.g., RA-VCH-2026-09-001" />
                </Form.Item>
                <Form.Item label="Release Attachments (Optional)">
                    <Upload
                        fileList={releaseAttachments}
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        multiple
                        maxCount={MAX_RELEASE_ATTACHMENTS}
                        beforeUpload={(file) => {
                            if (!RELEASE_ATTACHMENT_TYPES.includes(file.type)) {
                                message.error('Only PDF, JPG, PNG, or WEBP files are accepted.');
                                return Upload.LIST_IGNORE;
                            }
                            if (file.size / 1024 / 1024 > MAX_RELEASE_ATTACHMENT_MB) {
                                message.error(`Each attachment must be ${MAX_RELEASE_ATTACHMENT_MB} MB or smaller.`);
                                return Upload.LIST_IGNORE;
                            }
                            return false;
                        }}
                        onChange={({ fileList }) => setReleaseAttachments(fileList.slice(0, MAX_RELEASE_ATTACHMENTS))}
                    >
                        <Button icon={<UploadOutlined />}>Choose Attachments</Button>
                    </Upload>
                    <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        Up to 5 files, 10 MB each. Accepted: PDF, JPG, PNG, and WEBP.
                    </Text>
                </Form.Item>
            </Form>
        </Modal>
    </div>;
};

export default RiceAllowancePage;
